import { FilterMetadata, FilterService } from 'primeng/api';

import { TranslationDictionary } from '@shared/models';
import type { Filter } from '@shared/services';

import { getNestedField } from './data.utils';
import { getMaxTime, toDate } from './time.utils';

export class TableExtensions {
  private static readonly translationTextMatchModes: Record<string, { textMatchMode: string; every?: boolean }> = {
    translationStartsWith: { textMatchMode: 'startsWith' },
    translationContains: { textMatchMode: 'contains' },
    translationNotContains: { textMatchMode: 'notContains', every: true },
    translationEndsWith: { textMatchMode: 'endsWith' },
    translationEquals: { textMatchMode: 'equals' },
    translationNotEquals: { textMatchMode: 'notEquals', every: true },
  };

  static registerFilters(filterService: FilterService): void {
    this.betweenFilter(filterService);
    this.dateFilter(filterService);
    this.notInFilter(filterService);
    this.arrayInFilter(filterService);
    this.translationFilter(filterService);
  }

  static betweenFilter(filterService: FilterService): void {
    filterService.register('between', (cellValue: string | Date, filterValue: Date[]): boolean => {
      if (!filterValue || filterValue.length !== 2 || !filterValue[0]) {
        return true;
      }

      let endDay = getMaxTime(toDate(filterValue[0]));
      if (filterValue[1]) {
        endDay = getMaxTime(toDate(filterValue[1]));
      }

      const value = toDate(cellValue);
      return value.getTime() >= toDate(filterValue[0]).getTime() && value.getTime() <= endDay.getTime();
    });
  }

  static dateFilter(filterService: FilterService): void {
    filterService.register('dateIs', (cellValue: string | Date, filterValue: string | Date): boolean => {
      const value = this.toFilterDate(cellValue);
      const filter = this.toFilterDate(filterValue);

      return !filter || (!!value && value.toDateString() === filter.toDateString());
    });

    filterService.register('dateIsNot', (cellValue: string | Date, filterValue: string | Date): boolean => {
      const value = this.toFilterDate(cellValue);
      const filter = this.toFilterDate(filterValue);

      return !filter || (!!value && value.toDateString() !== filter.toDateString());
    });

    filterService.register('dateBefore', (cellValue: string | Date, filterValue: string | Date): boolean => {
      const value = this.toFilterDate(cellValue);
      const filter = this.toFilterDate(filterValue);

      return !filter || (!!value && value.getTime() < filter.getTime());
    });

    filterService.register('dateAfter', (cellValue: string | Date, filterValue: string | Date): boolean => {
      const value = this.toFilterDate(cellValue);
      const filter = this.toFilterDate(filterValue);

      return !filter || (!!value && value.getTime() > filter.getTime());
    });
  }

  static notInFilter(filterService: FilterService): void {
    filterService.register('notIn', (value: unknown, filter: unknown[]): boolean => {
      if (!filter?.length) return true;

      return !filter.includes(value);
    });
  }

  static arrayInFilter(filterService: FilterService): void {
    filterService.register('arrayIn', (value: unknown, filter: { value: unknown[]; field: string }): boolean => {
      if (!filter || !filter?.value?.length) return true;

      const nestedValue = getNestedField(value, filter.field);

      if (Array.isArray(nestedValue)) {
        return nestedValue.some((v) => filter.value.includes(v));
      }

      return filter.value.includes(nestedValue);
    });
  }

  static translationFilter(filterService: FilterService): void {
    Object.entries(this.translationTextMatchModes).forEach(([translationMatchMode, options]) => {
      filterService.register(translationMatchMode, (value: unknown, filter: unknown, locale?: string) => {
        const textFilter = filterService.filters[options.textMatchMode] as (
          value: unknown,
          filter: unknown,
          locale?: string
        ) => boolean;
        if (this.isTranslationDictionary(value)) {
          return this.filterTranslationValues(value, filter, textFilter, locale, options.every);
        }

        return textFilter(value, filter, locale);
      });
    });
  }

  static filterArray<T>(filterService: FilterService, data: T[], filters: Filter): T[] {
    return data.filter((item) => {
      return Object.entries(filters).every(([field, meta]) => {
        const value = getNestedField(item, field);

        if (Array.isArray(meta)) {
          return meta.some((m) => filterService.filters[m.matchMode](value, m.value));
        }

        return filterService.filters[meta.matchMode](value, meta.value);
      });
    });
  }

  static globalFilter(filterService: FilterService, data: unknown[], fields: string[], value: string) {
    return data.filter((item) =>
      fields.some((field) => filterService.filters['contains'](getNestedField(item, field), value))
    );
  }

  // converts the table global filter into OR-ed api filters over the given fields;
  // they go first, so the column filters are AND-ed to the whole OR group
  static toApiGlobalFilters(filters: Filter | undefined, fields: string[]): Filter {
    const { global, ...columnFilters } = filters ?? {};
    const value = (global as FilterMetadata | undefined)?.value;
    if (typeof value !== 'string' || !value.trim()) {
      return columnFilters;
    }

    const globalFilters = Object.fromEntries(
      fields
        .filter((field) => (columnFilters[field] as FilterMetadata | undefined)?.value == null)
        .map((field) => [field, { matchMode: 'contains', value, operator: 'or' }])
    );

    return { ...globalFilters, ...columnFilters };
  }

  private static filterTranslationValues(
    value: TranslationDictionary,
    filter: unknown,
    textFilter: (value: unknown, filter: unknown, locale?: string) => boolean,
    locale?: string,
    every?: boolean
  ): boolean {
    if (!filter) return true;

    const values = this.getTranslationValues(value);
    return every
      ? values.every((translation) => textFilter(translation, filter, locale))
      : values.some((translation) => textFilter(translation, filter, locale));
  }

  private static getTranslationValues(value: TranslationDictionary): string[] {
    return Object.values(value).filter((translation): translation is string => !!translation);
  }

  private static toFilterDate(value: string | Date | unknown): Date | null {
    if (!value) return null;

    const date = toDate(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private static isTranslationDictionary(value: unknown): value is TranslationDictionary {
    return (
      !!value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.values(value).every((translation) => typeof translation === 'string')
    );
  }
}
